import { Component, ElementRef, computed, inject, signal, viewChild } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@cumlaude/shared-authentication';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { PasfotoComponent } from '../../../shared/components/pasfoto/pasfoto.component';
import { UserService } from '../../../services/user.service';
import { NavigatieVoorkeurService } from '../../../services/navigatie-voorkeur.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { ReleaseNotesDialogComponent } from '../../../dialogs/release-notes-dialog/release-notes-dialog.component';
import { ProfielMenuActie, ProfielMenuDialogComponent } from '../../../dialogs/profiel/profiel-menu-dialog/profiel-menu-dialog.component';

const rechtsUitgelijndePosities: ConnectedPosition[] = [
	{ originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 1 },
	{ originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -1 },
];

@Component({
	selector: 'app-profiel-menu',
	templateUrl: './profiel-menu.component.html',
	styleUrls: ['./profiel-menu.component.scss'],
	imports: [PasfotoComponent],
})
export class ProfielMenuComponent {
	private readonly dialog = inject(Dialog);
	private readonly overlay = inject(Overlay);
	private readonly authService = inject(AuthService);
	private readonly userService = inject(UserService);
	private readonly navigatieVoorkeurService = inject(NavigatieVoorkeurService);
	private readonly userAuthenticationService = inject(UserAuthenticationService);
	private readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);
	private readonly router = inject(Router);

	protected readonly begroetingsnaam = this.userService.begroetingsnaam;
	protected readonly afkorting = computed(() => this.userService.account()?.afkorting);

	readonly showDropdown = signal(false);

	readonly triggerHost = viewChild.required<ElementRef>('triggerHost');

	async openMenu() {
		if (this.showDropdown()) return;
		this.showDropdown.set(true);

		const triggerElement = this.triggerHost().nativeElement;
		const dialogRef = this.dialog.open<ProfielMenuActie>(ProfielMenuDialogComponent, {
			ariaLabel: 'Profielmenu',
			hasBackdrop: false,
			positionStrategy: this.overlay
				.position()
				.flexibleConnectedTo(triggerElement)
				.withFlexibleDimensions(false)
				.withPositions(rechtsUitgelijndePosities),
		});

		dialogRef.outsidePointerEvents.subscribe((event) => {
			if (triggerElement.contains(event.target as Node)) {
				event.stopPropagation();
			}
			dialogRef.close();
		});

		const actie = await firstValueFrom(dialogRef.closed);
		this.showDropdown.set(false);
		if (actie) this.verwerk(actie);
	}

	private verwerk(actie: ProfielMenuActie) {
		switch (actie) {
			case 'switch':
				this.switchSchool();
				break;
			case 'releasenotes':
				this.openReleaseNotes();
				break;
			case 'wachtwoord':
				this.wijzigWachtwoord();
				break;
			case 'beheer':
				this.router.navigate(['/beheer']);
				break;
			case 'terug':
				this.router.navigate(['/start']);
				break;
			case 'uitloggen':
				this.uitloggen();
				break;
			case 'navigatie-feedback':
				this.navigatieVoorkeurService.vraagRedenNaSwitch();
				break;
		}
	}

	private switchSchool() {
		const account = this.userService.account();
		if (account?.multiAccount) {
			this.authService.clearSessionStorage();
			window.open(`${this.envConfig.authUrl}/switch?subjectToImpersonate=${encodeURIComponent(account.credentialsSub!)}`, '_self');
		}
	}

	private wijzigWachtwoord() {
		window.open(`${this.envConfig.authUrl}/change-password`, '_self');
	}

	private uitloggen() {
		this.userAuthenticationService.clearSessionStorage();
		this.authService.logoff();
	}

	private openReleaseNotes() {
		this.dialog.open<void>(ReleaseNotesDialogComponent, {
			positionStrategy: this.overlay.position().flexibleConnectedTo(this.triggerHost().nativeElement).withPositions(rechtsUitgelijndePosities),
		});
	}
}
